import { create } from 'zustand';
import type { WhatsappConversation, WhatsappMessage } from '@marnic/shared';

interface WhatsappState {
  conversations: WhatsappConversation[];
  activeConversationId: string | null;
  messagesByConversation: Record<string, WhatsappMessage[]>;
  unreadTotal: number;

  setConversations: (list: WhatsappConversation[]) => void;
  setActiveConversation: (id: string | null) => void;
  addInboundMessage: (conversationId: string, message: WhatsappMessage) => void;
  addOutboundEcho: (conversationId: string, message: WhatsappMessage) => void;
  updateMessageStatus: (conversationId: string, messageId: string, updates: Partial<WhatsappMessage>) => void;
  setMessages: (conversationId: string, messages: WhatsappMessage[]) => void;
  markConversationRead: (conversationId: string) => void;
  updateConversation: (conv: WhatsappConversation) => void;
}

export const useWhatsapp = create<WhatsappState>((set) => ({
  conversations: [],
  activeConversationId: null,
  messagesByConversation: {},
  unreadTotal: 0,

  setConversations: (conversations) =>
    set({
      conversations,
      unreadTotal: conversations.reduce((sum, c) => sum + c.unreadCount, 0),
    }),

  setActiveConversation: (id) => set({ activeConversationId: id }),

  addInboundMessage: (conversationId, message) =>
    set((s) => {
      const prev = s.messagesByConversation[conversationId] ?? [];
      const convs = s.conversations.map((c) =>
        c.id === conversationId
          ? { ...c, unreadCount: c.unreadCount + 1, lastMessageAt: message.createdAt }
          : c,
      );
      return {
        messagesByConversation: { ...s.messagesByConversation, [conversationId]: [...prev, message] },
        conversations: convs,
        unreadTotal: convs.reduce((sum, c) => sum + c.unreadCount, 0),
      };
    }),

  addOutboundEcho: (conversationId, message) =>
    set((s) => {
      const prev = s.messagesByConversation[conversationId] ?? [];
      if (prev.some((m) => m.id === message.id)) return {};
      const convs = s.conversations.map((c) =>
        c.id === conversationId ? { ...c, lastMessageAt: message.createdAt } : c,
      );
      return {
        messagesByConversation: { ...s.messagesByConversation, [conversationId]: [...prev, message] },
        conversations: convs,
      };
    }),

  updateMessageStatus: (conversationId, messageId, updates) =>
    set((s) => {
      const prev = s.messagesByConversation[conversationId] ?? [];
      return {
        messagesByConversation: {
          ...s.messagesByConversation,
          [conversationId]: prev.map((m) => (m.id === messageId ? { ...m, ...updates } : m)),
        },
      };
    }),

  setMessages: (conversationId, messages) =>
    set((s) => ({
      messagesByConversation: { ...s.messagesByConversation, [conversationId]: messages },
    })),

  markConversationRead: (conversationId) =>
    set((s) => {
      const convs = s.conversations.map((c) =>
        c.id === conversationId ? { ...c, unreadCount: 0 } : c,
      );
      return {
        conversations: convs,
        unreadTotal: convs.reduce((sum, c) => sum + c.unreadCount, 0),
      };
    }),

  updateConversation: (conv) =>
    set((s) => {
      const exists = s.conversations.some((c) => c.id === conv.id);
      const convs = exists
        ? s.conversations.map((c) => (c.id === conv.id ? conv : c))
        : [conv, ...s.conversations];
      return {
        conversations: convs,
        unreadTotal: convs.reduce((sum, c) => sum + c.unreadCount, 0),
      };
    }),
}));
